import React, { useState } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface FAQ {
  id: number;
  category: string;
  question: string;
  answer: string;
}

interface Category {
  id: string;
  label: string;
  icon: string;
}

interface QuickAction {
  id: number;
  title: string;
  description: string;
  icon: string;
  color: string;
  onPress: () => void;
}

const HelpCenter: React.FC = () => {
  const colors = getThemeColors('dark');

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories: Category[] = [
    { id: 'all', label: 'All Topics', icon: '📚' },
    { id: 'account', label: 'Account', icon: '👤' },
    { id: 'chama', label: 'Chama', icon: '👥' },
    { id: 'payments', label: 'Payments', icon: '💳' },
    { id: 'security', label: 'Security', icon: '🔒' },
    { id: 'marketplace', label: 'Marketplace', icon: '🛒' },
  ];

  const faqData: FAQ[] = [
    {
      id: 1,
      category: 'account',
      question: 'How do I create a VaultKe account?',
      answer: 'To create an account:\n1. Download the VaultKe app\n2. Tap "Sign Up"\n3. Enter your phone number and email\n4. Verify your phone number with the SMS code\n5. Complete your profile information\n6. Set up your security PIN',
    },
    {
      id: 2,
      category: 'account',
      question: 'How do I reset my password?',
      answer: 'To reset your password:\n1. Go to the login screen\n2. Tap "Forgot Password"\n3. Enter your registered email address\n4. Check your email for reset instructions\n5. Follow the link to create a new password',
    },
    {
      id: 3,
      category: 'chama',
      question: 'What is a Chama?',
      answer: 'A Chama is a traditional savings group where members contribute money regularly. VaultKe digitizes this process, making it easier to:\n• Track contributions\n• Manage loans\n• Monitor group finances\n• Communicate with members\n• Access group funds securely',
    },
    {
      id: 4,
      category: 'chama',
      question: 'How do I join a Chama?',
      answer: 'To join a Chama:\n1. Browse available Chamas in the "Browse Chamas" section\n2. Or get invited by an existing member\n3. Review the Chama rules and contribution requirements\n4. Tap "Request to Join"\n5. Wait for approval from Chama administrators\n6. Once approved, you can start contributing',
    },
    {
      id: 5,
      category: 'chama',
      question: 'How do I create my own Chama?',
      answer: 'To create a Chama:\n1. Go to "My Chamas" and tap "Create New Chama"\n2. Fill in Chama details (name, description, rules)\n3. Set contribution amounts and frequency\n4. Define member roles and permissions\n5. Invite initial members\n6. Launch your Chama once you have enough members',
    },
    {
      id: 6,
      category: 'payments',
      question: 'How do I make contributions?',
      answer: 'To make contributions:\n1. Go to your Chama dashboard\n2. Tap "Contribute"\n3. Select contribution type (regular, special, etc.)\n4. Enter the amount\n5. Choose payment method (wallet, M-Pesa, bank)\n6. Confirm the transaction\n7. You\'ll receive a confirmation receipt',
    },
    {
      id: 7,
      category: 'payments',
      question: 'What payment methods are supported?',
      answer: 'VaultKe supports:\n• VaultKe Wallet (instant transfers)\n• M-Pesa (mobile money)\n• Bank transfers\n• Debit/Credit cards\n• Direct bank deposits\n\nAll transactions are secured with bank-level encryption.',
    },
    {
      id: 8,
      category: 'payments',
      question: 'How do I withdraw money?',
      answer: 'To withdraw money:\n1. Go to your Wallet\n2. Tap "Withdraw"\n3. Enter the amount\n4. Select withdrawal method (M-Pesa, bank account)\n5. Confirm your PIN\n6. Processing time: M-Pesa (instant), Bank (1-3 business days)',
    },
    {
      id: 9,
      category: 'security',
      question: 'Is my money safe with VaultKe?',
      answer: 'Yes, your money is protected by:\n• Bank-level encryption (256-bit SSL)\n• Secure payment processing\n• Licensed financial institution partnerships\n• Regular security audits\n• Two-factor authentication\n• Transaction monitoring\n• Insurance coverage for deposits',
    },
    {
      id: 10,
      category: 'security',
      question: 'How do I enable two-factor authentication?',
      answer: 'To enable 2FA:\n1. Go to Settings > Security Settings\n2. Toggle "Two-Factor Authentication"\n3. Choose your preferred method (SMS, email, or authenticator app)\n4. Follow the setup instructions\n5. Verify with a test code\n6. 2FA will be required for sensitive actions',
    },
  ];

  const quickActions: QuickAction[] = [
    {
      id: 1,
      title: 'Contact Support',
      description: 'Get personalized help',
      icon: '💬',
      color: colors.primary,
      onPress: () => window.location.href = '/dashboard/settings/contact-support',
    },
    {
      id: 2,
      title: 'Video Tutorials',
      description: 'Watch how-to guides',
      icon: '🎥',
      color: colors.info,
      onPress: () => alert('Video tutorials will be available soon!'),
    },
    {
      id: 3,
      title: 'Community Forum',
      description: 'Connect with other users',
      icon: '👥',
      color: colors.success,
      onPress: () => alert('Community forum will be available soon!'),
    },
  ];

  const filteredFAQs = faqData.filter(faq => {
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    const matchesSearch = searchQuery === '' ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleFAQ = (id: number) => {
    setExpandedFAQ(expandedFAQ === id ? null : id);
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    content: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: spacing.lg,
    },
    header: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      padding: spacing.xl,
      marginBottom: spacing.xl,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('lg'),
      textAlign: 'center',
    },
    headerIcon: {
      fontSize: 48,
      marginBottom: spacing.md,
    },
    headerTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    headerSubtitle: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    searchContainer: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      padding: spacing.xl,
      marginBottom: spacing.xl,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('lg'),
    },
    searchInput: {
      width: '100%',
      padding: spacing.md,
      borderRadius: borderRadius.md,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.background,
      color: colors.text,
      fontSize: typography.fontSize.base,
      outline: 'none',
    },
    section: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      padding: spacing.xl,
      marginBottom: spacing.xl,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('lg'),
    },
    sectionTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.lg,
      paddingBottom: spacing.md,
      borderBottom: `2px solid ${colors.primary}`,
    },
    quickActionsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: spacing.lg,
    },
    quickActionCard: {
      display: 'flex',
      alignItems: 'center',
      padding: spacing.lg,
      borderRadius: borderRadius.lg,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
      cursor: 'pointer',
      ...getShadowStyle('md'),
      transition: 'all 0.3s ease',
    },
    quickActionIcon: {
      width: 56,
      height: 56,
      borderRadius: 28,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: spacing.lg,
      fontSize: 24,
    },
    quickActionContent: {
      flex: 1,
    },
    quickActionTitle: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    quickActionDescription: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      lineHeight: 1.4,
    },
    quickActionArrow: {
      color: colors.primary,
      fontSize: 20,
      fontWeight: 'bold',
    },
    categoriesContainer: {
      display: 'flex',
      gap: spacing.md,
      overflowX: 'auto',
      paddingBottom: spacing.sm,
    },
    categoryChip: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.sm,
      padding: `${spacing.sm} ${spacing.md}`,
      borderRadius: borderRadius.full,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      ...getShadowStyle('sm'),
      transition: 'all 0.2s ease',
    },
    categoryChipSelected: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
      color: colors.white,
    },
    categoryIcon: {
      fontSize: 16,
    },
    categoryText: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.bold,
    },
    faqItem: {
      border: `1px solid ${colors.border}`,
      borderRadius: borderRadius.lg,
      marginBottom: spacing.md,
      overflow: 'hidden',
      ...getShadowStyle('sm'),
    },
    faqHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: spacing.lg,
      cursor: 'pointer',
      backgroundColor: colors.surface,
      borderBottom: expandedFAQ ? `1px solid ${colors.border}` : 'none',
    },
    faqQuestion: {
      flex: 1,
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginRight: spacing.md,
    },
    faqToggle: {
      fontSize: 18,
      color: colors.primary,
      fontWeight: 'bold',
    },
    faqAnswer: {
      padding: spacing.lg,
      paddingTop: 0,
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      lineHeight: 1.6,
      whiteSpace: 'pre-line',
    },
    noResults: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: spacing.xxxl,
    },
    noResultsIcon: {
      fontSize: 64,
      marginBottom: spacing.lg,
      opacity: 0.5,
    },
    noResultsText: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.textSecondary,
      marginBottom: spacing.sm,
    },
    noResultsSubtext: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: 1.5,
    },
  };

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Help Center"
        subtitle="Find answers to frequently asked questions"
        showBackButton={true}
        showHomeButton={true}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerIcon}>❓</div>
            <h1 style={styles.headerTitle}>Help Center</h1>
            <p style={styles.headerSubtitle}>Find answers to frequently asked questions</p>
          </div>

          {/* Search */}
          <div style={styles.searchContainer}>
            <input
              type="text"
              placeholder="Search FAQs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
            />
          </div>

          {/* Quick Actions */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Quick Actions</h2>
            <div style={styles.quickActionsGrid}>
              {quickActions.map((action) => (
                <div
                  key={action.id}
                  style={styles.quickActionCard}
                  onClick={action.onPress}
                >
                  <div
                    style={{
                      ...styles.quickActionIcon,
                      backgroundColor: action.color + '20',
                      color: action.color,
                    }}
                  >
                    {action.icon}
                  </div>
                  <div style={styles.quickActionContent}>
                    <div style={styles.quickActionTitle}>{action.title}</div>
                    <div style={styles.quickActionDescription}>{action.description}</div>
                  </div>
                  <span style={styles.quickActionArrow}>▶️</span>
                </div>
              ))}
            </div>
          </div>

          {/* Categories */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Browse by Category</h2>
            <div style={styles.categoriesContainer}>
              {categories.map((category) => (
                <div
                  key={category.id}
                  style={{
                    ...styles.categoryChip,
                    ...(selectedCategory === category.id ? styles.categoryChipSelected : {}),
                  }}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  <span style={styles.categoryIcon}>{category.icon}</span>
                  <span style={styles.categoryText}>{category.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              {selectedCategory === 'all' ? 'All Questions' : `${categories.find(c => c.id === selectedCategory)?.label} Questions`}
              {searchQuery && ` (${filteredFAQs.length} results)`}
            </h2>
            {filteredFAQs.length > 0 ? (
              filteredFAQs.map((item) => (
                <div key={item.id} style={styles.faqItem}>
                  <div
                    style={styles.faqHeader}
                    onClick={() => toggleFAQ(item.id)}
                  >
                    <span style={styles.faqQuestion}>{item.question}</span>
                    <span style={styles.faqToggle}>
                      {expandedFAQ === item.id ? '−' : '+'}
                    </span>
                  </div>
                  {expandedFAQ === item.id && (
                    <div style={styles.faqAnswer}>{item.answer}</div>
                  )}
                </div>
              ))
            ) : (
              <div style={styles.noResults}>
                <div style={styles.noResultsIcon}>🔍</div>
                <div style={styles.noResultsText}>No results found for "{searchQuery}"</div>
                <div style={styles.noResultsSubtext}>
                  Try different keywords or contact support for help
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;